import { Link } from '@tanstack/react-router'
import { Lock, LockOpen, Clock, Star, ChevronDown } from 'lucide-react'
import { clsx } from 'clsx'
import { useAppStore } from '@/shared/stores/appStore'
import { formatCountdown } from '@/shared/lib/formatCountdown'
import { CompetitionStatus, LeagueFormat, TransferWindowState, type LeagueSummary } from '../types'
import { MiniFormationPitch } from './MiniFormationPitch'
import styles from './LeagueSummaryCard.module.css'

type LeagueSummaryCardProps = {
  league:          LeagueSummary
  now:             number
  isFavorite:      boolean
  onToggleFavorite: () => void
  isExpanded:      boolean
  onTogglePreview: () => void
}


const SPORT_ICON: Record<string, string> = { FOOTBALL: '⚽', CS2: '🎮' }

const BRT = 'America/Sao_Paulo'

function formatSavedAt(isoUtc: string): string {
  const d = new Date(isoUtc)
  const date = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: BRT })
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: BRT })
  return `${date} às ${time}`
}

function StatusStrip({ league, now }: { league: LeagueSummary; now: number }) {
  const isOpen = league.windowState === TransferWindowState.Open

  if (league.competitionStatus === CompetitionStatus.NotStarted && !league.windowChangesAt) {
    return (
      <div className={clsx(styles.strip, styles.stripPre)}>
        <Clock size={13} className={styles.stripIcon} aria-hidden="true" />
        <span className={styles.stripText}>Competição em breve</span>
      </div>
    )
  }

  const countdown = league.windowChangesAt
    ? formatCountdown(league.windowChangesAt, now)
    : null

  return (
    <div className={clsx(styles.strip, isOpen ? styles.stripOpen : styles.stripClosed)}>
      {isOpen
        ? <LockOpen size={13} className={styles.stripIcon} aria-hidden="true" />
        : <Lock     size={13} className={styles.stripIcon} aria-hidden="true" />}
      <span className={styles.stripText}>
        {isOpen ? 'Janela aberta' : 'Janela fechada'}
      </span>
      {countdown && (
        <span className={styles.stripCountdown}>
          {isOpen ? 'Fecha em ' : 'Abre em '}
          <span className={styles.stripTime}>{countdown}</span>
        </span>
      )}
    </div>
  )
}

export function LeagueSummaryCard({
  league,
  now,
  isFavorite,
  onToggleFavorite,
  isExpanded,
  onTogglePreview,
}: LeagueSummaryCardProps) {
  const userId   = useAppStore((s) => s.session?.user.id)
  const isH2h    = league.format === LeagueFormat.HeadToHead
  const isPreComp = league.competitionStatus === CompetitionStatus.NotStarted

  const primaryPoints = isH2h ? (league.h2hPoints ?? 0) : league.totalPoints
  const primaryLabel  = isH2h ? 'H2H' : 'TOTAL'

  const isFirst = league.rank === 1
  const isLast  = league.rank !== null && league.rank === league.memberCount

  const sportIcon = SPORT_ICON[league.competitionSport] ?? '🏆'
  const gwLabel   = league.currentGameweek !== null ? `R${league.currentGameweek}` : 'RODADA'

  const isPreStart  = league.windowState === TransferWindowState.Open && isPreComp
  const isWaiting   = !isPreStart && isPreComp
  const hasSquad    = league.squadPreview.length > 0

  return (
    <div className={clsx(styles.card, league.isPaidLeague && styles.isPaid)}>

      {/* Rank badge — absolute top-right, hidden pre-competition */}
      {league.rank !== null && (
        <div className={clsx(
          styles.rankBadge,
          isFirst && styles.rankBadgeFirst,
          isLast  && styles.rankBadgeLast,
        )}>
          {league.rank}º
        </div>
      )}

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.compLabel}>
          {sportIcon} {league.competitionName}
        </div>
        <Link
          to="/league/$leagueId"
          params={{ leagueId: league.id }}
          search={{ tab: 'tabela', gw: undefined }}
          className={styles.leagueName}
        >
          {league.name}
        </Link>
        <div className={styles.headerMeta}>
          <span className={styles.formatBadge}>
            {isH2h ? 'H2H' : 'PONTOS'}
          </span>
          <button
            type="button"
            className={clsx(styles.starBtn, isFavorite && styles.starActive)}
            onClick={onToggleFavorite}
            aria-label={isFavorite ? 'Remover dos favoritos' : 'Marcar como favorita'}
            aria-pressed={isFavorite}
          >
            <Star size={15} fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Stats — pre-start shows CTA pill instead */}
      {isPreComp ? (
        <div className={styles.ctaZone}>
          {isPreStart && hasSquad ? (
            <>
              <div className={clsx(styles.ctaPill, styles.ctaPillEscalado)}>
                ✅ Time escalado
              </div>
              {league.squadUpdatedAt && (
                <span className={styles.ctaSub}>
                  em {formatSavedAt(league.squadUpdatedAt)}
                </span>
              )}
            </>
          ) : (
            <>
              <div className={clsx(styles.ctaPill, isWaiting && styles.ctaPillWaiting)}>
                {isPreStart ? '🟢 Monte sua escalação' : '⏳ Aguardando GW1'}
              </div>
              <span className={styles.ctaSub}>
                {isPreStart
                  ? 'Escale antes do início da competição'
                  : 'A janela será aberta após a rodada 1'}
              </span>
            </>
          )}
        </div>
      ) : (
        <div className={styles.statsArea}>
          <div className={styles.statBlock}>
            <span className={styles.statValueLg}>{primaryPoints}</span>
            <span className={styles.statLabel}>{primaryLabel}</span>
            {league.pointsGapAbove !== null && (
              <div className={clsx(
                styles.pointsGap,
                league.pointsGapAbove >= 0 ? styles.gapAhead : styles.gapBehind,
              )}>
                <span className={styles.gapDelta}>
                  {league.pointsGapAbove >= 0
                    ? `△ +${league.pointsGapAbove}`
                    : `△ ${league.pointsGapAbove}`}
                </span>
                <span className={styles.gapText}>
                  {league.pointsGapAbove >= 0 ? 'de folga' : 'para subir'}
                </span>
              </div>
            )}
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statBlock}>
            <span className={styles.statValueSm}>{league.gwPoints}</span>
            <span className={styles.statLabel}>{gwLabel}</span>
          </div>
        </div>
      )}

      {/* Preview toggle */}
      <button
        type="button"
        className={styles.previewToggle}
        onClick={onTogglePreview}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Fechar' : 'Ver'} escalação de ${league.name}`}
      >
        <span>Minha escalação</span>
        <ChevronDown
          size={14}
          className={clsx(styles.toggleChevron, isExpanded && styles.toggleChevronOpen)}
          aria-hidden="true"
        />
      </button>

      {/* Formation preview — rendered inside card, card grows vertically */}
      {isExpanded && (
        <MiniFormationPitch squadPreview={league.squadPreview} />
      )}

      {/* Status strip */}
      <StatusStrip league={league} now={now} />

      {/* Footer */}
      <div className={styles.cardFooter}>
        <Link
          to="/league/$leagueId"
          params={{ leagueId: league.id }}
          search={{ tab: 'tabela', gw: undefined }}
          className={styles.footerLink}
          aria-label={`Ver liga ${league.name}`}
        >
          Ver liga →
        </Link>
        {userId && (
          <Link
            to="/league/$leagueId"
            params={{ leagueId: league.id }}
            search={{ tab: 'escalacoes', gw: undefined }}
            className={clsx(styles.footerBtn, isPreStart && styles.footerBtnAccent)}
            aria-label={`${isPreStart ? 'Escalar' : 'Ver escalação'} em ${league.name}`}
          >
            {isPreStart ? 'Escalar agora' : 'Escalação'}
          </Link>
        )}
      </div>
    </div>
  )
}
